import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";
import { connect } from "react-redux";
import { intlShape, injectIntl } from "react-intl";
import { Button } from "cxa-components";
import { AlertModal, Modal } from "@cxagroup/casper";
import {
  setEthnicity,
  uploadImage,
  getImage,
  reAge,
  confirmCameraPhoto,
  confirmUploadPhoto,
  resetFaceAgeingPage,
  setAgeFaceParams,
  getAgingStatus,
} from "actions/faceAgeingActions";
import messages from "components/messagesDescription";
import { isExist, compose } from "cxa-utils";
import LifestyleAgeingNav from "./LifestyleAgeingNav";
import { load } from "actions/userProfileActions";

import TakePhotoForm from "./TakePhotoForm";
import PhotoGuidelines from "./photoGuidelines";
import CropGuidelines from "./cropGuidelines";
import UploadPhotoForm from "./uploadPhotoForm";
import CropImageForm from "./cropImageForm";
import { SupportsWebcam } from "../../common/WebCam";

const FACE_AGING_STATUS = {
  FIRST_TIME: "FIRST_TIME",
  REVISIT_AND_REANSWER: "REVISIT_AND_REANSWER",
  REVISIT_AND_USE_EXISTING_ANSWER: "REVISIT_AND_USE_EXISTING_ANSWER",
};

class PhotoUploadPage extends PureComponent {
  state = {
    mode: SupportsWebcam ? "init" : "upload",
    ethnicityId: 1,
    showModal: false,
    faceAgeingStatus: null, // value is FACE_AGING_STATUS
    callingGetAgingStatus: false,
    hraAlertOpen: false,
  };

  componentDidMount() {
    const { clientSettings } = this.props;
    const { hraDisclaimerNote } = clientSettings;
    this.props.load();

    if (isExist(hraDisclaimerNote)) {
      this.setState({
        hraAlertOpen: true,
      });
    }
  }

  // eslint-disable-next-line
  componentWillReceiveProps(nextProps) {
    const {
      uploadPhotoCompleted,
      uploadImageResponse,
      takePhotoCompleted,
      getAgingStatusResponse,
      submitting,
    } = nextProps;

    if ((uploadPhotoCompleted || takePhotoCompleted) && !this.state.callingGetAgingStatus) {
      // Check whether the user has completed HRA questionnaire
      this.setState({ callingGetAgingStatus: true });
      this.props.getAgingStatus();
    }

    if (getAgingStatusResponse && getAgingStatusResponse.assessmentStatus && !submitting) {
      if (getAgingStatusResponse.assessmentStatus.hraQuestionsAnswered === "true") {
        this.handleOpenModal();
      } else {
        // First time visit
        this.setState({ faceAgeingStatus: FACE_AGING_STATUS.FIRST_TIME });
      }
    }

    if (uploadImageResponse && uploadImageResponse.image && this.state.mode == "upload") {
      this.setState({ mode: "crop" });
    }
  }

  handleCloseHRAModal = () => this.setState({ hraAlertOpen: false });

  handleOpenModal = () => {
    this.setState({ showModal: true });
  };

  handleCloseModal = () => {
    this.setState({ showModal: false });
  };

  handleModalRespondYes = () => {
    this.setState({
      faceAgeingStatus: FACE_AGING_STATUS.REVISIT_AND_USE_EXISTING_ANSWER,
    });
    this.props.reAge({ requireHRA: false });
  };

  handleModalRespondNo = () => {
    this.setState({ faceAgeingStatus: FACE_AGING_STATUS.REVISIT_AND_REANSWER });
    this.props.reAge({ requireHRA: true });
  };

  handleChangeEthnicity = event => {
    if (event) {
      event.preventDefault();
    }

    this.setState({ ethnicityId: event.target.value });
  };

  handleSwitchToUploadPhoto = event => {
    if (event) {
      event.preventDefault();
    }

    this.setState({ mode: "upload" });
  };

  handleCancelUpload = event => {
    if (event) {
      event.preventDefault();
    }

    this.setState({ mode: "init" });
  };

  goToHRA() {
    const { push } = this.props;
    push("/wellness/lifestyleAgeing/questionnaire");
  }

  handleSkip = event => {
    if (event) {
      event.preventDefault();
    }
    this.goToHRA();
  };

  handleReUpload = event => {
    if (event) {
      event.preventDefault();
    }

    this.props.resetFaceAgeingPage();
    this.setState({ mode: "upload" });
  };

  render() {
    const { faceAgeingStatus, hraAlertOpen } = this.state;

    if (faceAgeingStatus == FACE_AGING_STATUS.FIRST_TIME) {
      this.props.resetFaceAgeingPage();
      return <Redirect to="/wellness/lifestyleAgeing/questionnaire" />;
    } else if (faceAgeingStatus == FACE_AGING_STATUS.REVISIT_AND_REANSWER) {
      if (this.props.reAgeResponse && this.props.reAgeResponse.status == "success") {
        this.props.resetFaceAgeingPage();
        return <Redirect to="/wellness/lifestyleAgeing/questionnaire" />;
      }
    } else if (faceAgeingStatus == FACE_AGING_STATUS.REVISIT_AND_USE_EXISTING_ANSWER) {
      if (this.props.reAgeResponse && this.props.reAgeResponse.status == "success") {
        this.props.resetFaceAgeingPage();
        return <Redirect to="/wellness/lifestyleAgeing/results" />;
      }
    }

    let form;
    let guidelines;

    switch (this.state.mode) {
      case "upload":
        form = (
          <UploadPhotoForm
            {...this.props}
            handleCancelUpload={this.handleCancelUpload}
            handleSkip={this.handleSkip}
            isWebcamSupported={SupportsWebcam}
          />
        );
        guidelines = <PhotoGuidelines />;
        break;

      case "crop":
        form = (
          <CropImageForm
            {...this.props}
            ethnicity={this.state.ethnicityId}
            handleChangeEthnicity={this.handleChangeEthnicity}
            handleReUpload={this.handleReUpload}
          />
        );
        guidelines = <CropGuidelines />;
        break;

      case "init":
      default:
        form = (
          <TakePhotoForm
            {...this.props}
            {...this.props}
            ethnicity={this.state.ethnicityId}
            handleChangeEthnicity={this.handleChangeEthnicity}
            handleSwitchToUploadPhoto={this.handleSwitchToUploadPhoto}
            handleSkip={this.handleSkip}
          />
        );
        guidelines = <PhotoGuidelines />;
    }

    const intl = this.props.intl;
    return (
      <div className="apl-photo">
        <LifestyleAgeingNav />
        <h1>{intl.formatMessage(messages.photoUploadDescription)}</h1>
        <div className="grid__wrapper">
          <div className="grid__item grid__item--six">{form}</div>
          <div className="grid__item grid__item--six">{guidelines}</div>
        </div>
        <p>
          {this.props.ageFaceResponse && (
            <span>
              {intl.formatMessage({
                id: this.props.ageFaceResponse.status,
                defaultMessage: this.props.ageFaceResponse.status,
              })}
            </span>
          )}
        </p>
        <Modal
          isOpen={this.state.showModal}
          onRequestClose={this.handleCloseModal}
          contentLabel="HRA response to age photo"
        >
          <div className="modal__body">
            <p>{intl.formatMessage(messages.useExistingDescription)}</p>
            <p>{intl.formatMessage(messages.toProcessDescription)}</p>
          </div>
          <div className="modal__footer">
            <Button primary onClick={this.handleModalRespondYes}>
              {intl.formatMessage(messages.yesTextDescription)}
            </Button>
            <Button secondary onClick={this.handleModalRespondNo}>
              {intl.formatMessage(messages.noTextDescription)}
            </Button>
            <Button secondary onClick={this.handleCloseModal}>
              {intl.formatMessage(messages.cancelTextDescription)}
            </Button>
          </div>
        </Modal>

        <AlertModal
          key="HRA_ALERT"
          isOpen={hraAlertOpen}
          title=""
          type={AlertModal.types.INFO}
          body={intl.formatMessage(messages.hraDisclaimerText)}
          footer={
            <Button primary type="button" onClick={this.handleCloseHRAModal}>
              {intl.formatMessage(messages.okTextDescription)}
            </Button>
          }
        />
      </div>
    );
  }
}

PhotoUploadPage.propTypes = {
  ageFaceResponse: PropTypes.object,
  resetFaceAgeingPage: PropTypes.func,
  push: PropTypes.func,
  uploadPhotoCompleted: PropTypes.bool,
  takePhotoCompleted: PropTypes.bool,
  uploadImageResponse: PropTypes.object,
  intl: intlShape.isRequired,
  getAgingStatus: PropTypes.func,
  getAgingStatusResponse: PropTypes.object,
  reAge: PropTypes.func,
  reAgeResponse: PropTypes.object,
  clientSettings: PropTypes.object,
  load: PropTypes.func,
};

const mapStateToProps = ({ faceAgeing, common }) => {
  const {
    setEthnicityResponse,
    uploadImageResponse,
    ageFaceResponse,
    getImageResponse,
    reAgeResponse,
    getAgingStatusResponse,
    uploadPhotoCompleted,
    takePhotoCompleted,
    ageFaceParams,
    submitting,
    load,
  } = faceAgeing;
  const { clientSettings } = common;

  return {
    setEthnicityResponse,
    uploadImageResponse,
    ageFaceResponse,
    getImageResponse,
    reAgeResponse,
    getAgingStatusResponse,
    uploadPhotoCompleted,
    takePhotoCompleted,
    ageFaceParams,
    submitting,
    clientSettings,
    load,
  };
};

const mapDispatchToProps = {
  setEthnicity,
  uploadImage,
  getImage,
  reAge,
  confirmCameraPhoto,
  confirmUploadPhoto,
  resetFaceAgeingPage,
  setAgeFaceParams,
  getAgingStatus,
  load,
};

const PhotoUploadPageContainer = compose(
  injectIntl,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(PhotoUploadPage);

export default PhotoUploadPageContainer;



// WEBPACK FOOTER //
// ./components/wellness/lifestyleAgeing/photoUploadPage.jsx